import { useMemo } from 'react'
import { formatInTimeZone } from 'date-fns-tz'
import { useLocation } from 'react-router-dom'
import { useAppQuery } from '@/helpers/api/query'

import { createInstance } from '@/helpers/api/base'
import { Disruption } from '@/types/disruptions'
import { useWorkstationStore } from '@/modules/Workstation/store'
import { useGarageStore } from '@/modules/FindGarage'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'
import { useReaderLogStore } from '@/modules/FindControllerLog/store'
import { ROUTES } from '@/router'
import { useDateStore } from '@/components/DateInput'
import { isToday } from 'date-fns'

const GET_DISRUPTIONS_KEY = 'GET_DISRUPTIONS'

export const useGetDisruptions = () => {
    const { routes } = useWorkstationStore()
    const { garage } = useGarageStore()
    const { source } = useReaderLogStore()
    const { role } = useAuthStore()
    const { pathname } = useLocation()
    const { date } = useDateStore()

    const isTodayDate = isToday(date)
    const isAllDisruptionsPage = pathname.includes(ROUTES.ALL_DISRUPTIONS)
    const params = useMemo(() => {
        const formattedDate = formatInTimeZone(date, 'UTC', "yyyy-MM-dd'T'HH:mm:ss")

        switch (role) {
            case Role.Controller: {
                return [routes.map((route) => route.route), formattedDate]
            }
            case Role.MileageClerk: {
                return [garage?.routes]
            }
            case Role.Reader: {
                return isAllDisruptionsPage ? [] : [source?.routes.map((route) => route.route)]
            }
            case Role.PerformanceManager: {
                return isAllDisruptionsPage ? [] : [routes?.map((route) => route.route), formattedDate]
            }
            default: {
                return []
            }
        }
    }, [role, garage, routes, source, isAllDisruptionsPage, date])

    return useAppQuery<Disruption[]>({
        queryKey: [GET_DISRUPTIONS_KEY, ...params],
        queryFn: ({ queryKey, signal }) =>
            createInstance({
                url: '/disruptions',
                method: 'GET',
                params: {
                    Routes: queryKey[1] as string[],
                    Date: isTodayDate ? null : (queryKey[2] as string),
                    IsActive: isTodayDate ? true : null,
                },
                paramsSerializer: {
                    indexes: null,
                },
                signal,
            }),
        select: (data) =>
            role === Role.PerformanceManager
                ? data
                : data.filter((item) => !!item.routes.length && !!item.location && !!item.start && !!item.end),
        enabled:
            (role && [Role.PerformanceManager, Role.Reader].includes(role) && isAllDisruptionsPage) || !!params?.length,
    })
}
